import * as THREE from 'three';
import { DirtyRange, FOG_FRAG, FOG_VERT, fxUniforms, quadGeometry } from './common';

export interface GlowOpts { size: number; opacity?: number; flicker?: number; phase?: number }

/**
 * Additive glow billboards (or ground-aligned light pools) — all of them in a
 * single draw call. Replaces dozens of Sprite objects and per-relic point lights.
 * Colours are linear HDR; values above 1 feed the bloom.
 */
export class Glows {
  readonly mesh: THREE.Mesh;
  private readonly pos: THREE.InstancedBufferAttribute;
  private readonly col: THREE.InstancedBufferAttribute;
  private readonly misc: THREE.InstancedBufferAttribute;
  private readonly geo: THREE.InstancedBufferGeometry;
  private readonly positions = new DirtyRange();
  private readonly colors = new DirtyRange();
  private readonly sizes = new DirtyRange();
  private readonly posAttrs: THREE.InstancedBufferAttribute[];
  private readonly colAttrs: THREE.InstancedBufferAttribute[];
  private readonly miscAttrs: THREE.InstancedBufferAttribute[];
  private count = 0;
  private free: number[] = [];

  constructor(readonly capacity: number, ground = false) {
    const g = (this.geo = quadGeometry());
    this.pos = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3).setUsage(THREE.DynamicDrawUsage);
    this.col = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(THREE.DynamicDrawUsage);
    this.misc = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3).setUsage(THREE.DynamicDrawUsage);
    this.posAttrs = [this.pos]; this.colAttrs = [this.col]; this.miscAttrs = [this.misc];
    g.setAttribute('iPos', this.pos); g.setAttribute('iCol', this.col); g.setAttribute('iMisc', this.misc);
    g.instanceCount = 0;
    const mat = new THREE.ShaderMaterial({
      uniforms: fxUniforms,
      defines: ground ? { GROUND: '' } : {},
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute vec3 iPos; attribute vec4 iCol; attribute vec3 iMisc;
        uniform float uTime;
        varying vec2 vUv; varying vec4 vCol;
        ${FOG_VERT}
        void main() {
          if (iCol.a <= 0.0001 || iMisc.x <= 0.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
          vUv = uv * 2.0 - 1.0;
          float f = 1.0 + iMisc.y * (0.6 * sin(uTime * 2.1 + iMisc.z) + 0.4 * sin(uTime * 5.3 + iMisc.z * 2.0));
          vCol = vec4(iCol.rgb, iCol.a * f);
          #ifdef GROUND
            vec4 mv = modelViewMatrix * vec4(iPos + vec3(position.x, 0.0, -position.y) * iMisc.x, 1.0);
          #else
            vec4 mv = modelViewMatrix * vec4(iPos, 1.0);
            mv.xy += position.xy * iMisc.x;
          #endif
          FOG_FROM_MV(mv)
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        varying vec2 vUv; varying vec4 vCol;
        ${FOG_FRAG}
        void main() {
          float d = length(vUv);
          if (d > 1.0) discard;
          float a = exp(-3.6 * d) * (1.0 - d * d);
          gl_FragColor = vec4(vCol.rgb * (a * vCol.a * fogKeep()), 1.0);
        }`,
    });
    this.mesh = new THREE.Mesh(g, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = ground ? 2 : 5;
  }

  add(color: THREE.ColorRepresentation, x: number, y: number, z: number, o: GlowOpts): number {
    const i = this.free.pop() ?? this.count++;
    if (i >= this.capacity) throw new Error('Glows capacity exceeded');
    this.geo.instanceCount = Math.max(this.geo.instanceCount, i + 1);
    this.setPos(i, x, y, z);
    this.setColor(i, color, o.opacity ?? 1);
    this.misc.setXYZ(i, o.size, o.flicker ?? 0, o.phase ?? Math.random() * 6.28);
    this.sizes.mark(i);
    return i;
  }

  remove(i: number) { this.setOpacity(i, 0); this.setSize(i, 0); this.free.push(i); }

  setPos(i: number, x: number, y: number, z: number) {
    if (this.pos.getX(i) === Math.fround(x) && this.pos.getY(i) === Math.fround(y) && this.pos.getZ(i) === Math.fround(z)) return;
    this.pos.setXYZ(i, x, y, z); this.positions.mark(i);
  }
  setColor(i: number, color: THREE.ColorRepresentation, opacity: number) {
    const c = _c.set(color);
    if (this.col.getX(i) === Math.fround(c.r) && this.col.getY(i) === Math.fround(c.g) && this.col.getZ(i) === Math.fround(c.b) && this.col.getW(i) === Math.fround(opacity)) return;
    this.col.setXYZW(i, c.r, c.g, c.b, opacity); this.colors.mark(i);
  }
  setOpacity(i: number, opacity: number) {
    if (this.col.getW(i) === Math.fround(opacity)) return;
    this.col.setW(i, opacity); this.colors.mark(i);
  }
  setSize(i: number, size: number) {
    if (this.misc.getX(i) === Math.fround(size)) return;
    this.misc.setX(i, size); this.sizes.mark(i);
  }

  flush() { this.positions.flush(this.posAttrs); this.colors.flush(this.colAttrs); this.sizes.flush(this.miscAttrs); }
}
const _c = new THREE.Color();
